/**
 * Tabbed category showcase: pill buttons switch which category panel (media +
 * heading + CTA) is visible. Panels stack in one grid cell and cross-fade.
 *
 * The active pill is a single "thumb" element that slides and resizes between
 * the tabs rather than a background that snaps on each button. The thumb is
 * measured from the active tab's box, so it tracks the differing label widths.
 * A CSS class gates the transition on, so the very first placement is instant;
 * before the script upgrades the element an [aria-selected] background provides
 * the identical pill (progressive enhancement, no flash of unstyled pill).
 */
class OracuraCategoryShowcase extends HTMLElement {
  connectedCallback() {
    this.lifecycle?.abort();
    this.lifecycle = new AbortController();
    const signal = this.lifecycle.signal;
    this.tabs = Array.from(this.querySelectorAll('[data-category-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-category-panel]'));
    this.tabBar = this.querySelector('[data-category-tabs]');
    this.thumb = this.querySelector('[data-category-thumb]');
    this.activeIndex = Math.max(
      0,
      this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'),
    );

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.activate(i), { signal });
      tab.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (i + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.activate(next); this.tabs[next].focus();
      }, { signal });
    });

    // The panels only take turns on a phone. From 750px up the stylesheet shows
    // every one of them at once, so the switcher's hidden state has to be lifted
    // there or the page ships visible, tabbable panels that are marked hidden
    // from assistive technology.
    this.wide = window.matchMedia('(min-width: 750px)');
    this.wide.addEventListener?.('change', () => this.syncPanels(), { signal });
    this.activate(this.activeIndex);

    if (this.thumb && this.tabBar) {
      this.moveThumb();
      // Hand the pill over from the CSS fallback to the thumb, then enable the
      // slide on the next frame so this initial placement doesn't animate.
      this.tabBar.classList.add('is-thumbed');
      requestAnimationFrame(() => this.tabBar.classList.add('is-animated'));

      // Label widths change when the brand webfont finishes loading, and the
      // bar's available width changes on rotate/resize — re-measure on both.
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.moveThumb());
      }
      window.addEventListener('resize', () => {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.moveThumb());
      }, { signal });
    }
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    cancelAnimationFrame(this.raf);
  }

  activate(index) {
    this.activeIndex = index;
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      tab.tabIndex = i === index ? 0 : -1;
    });
    this.syncPanels();
    this.moveThumb();
  }

  /**
   * Keeps what the panels expose in step with what they actually show.
   *
   * `inert` is set alongside aria-hidden because hiding a panel visually is not
   * enough on its own: without it the links inside a faded-out panel stay in the
   * tab order, so keyboard focus disappears into a panel nobody can see.
   */
  syncPanels() {
    const stacked = !this.wide?.matches;
    this.panels.forEach((panel, i) => {
      const hidden = stacked && i !== this.activeIndex;
      panel.setAttribute('aria-hidden', hidden ? 'true' : 'false');
      panel.inert = hidden;
      // A tab panel only where there are tabs: on a phone. From 750px up both
      // banners show and the tab row is hidden, so they are plain regions.
      if (stacked && panel.dataset.tabId) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', panel.dataset.tabId);
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
      }
    });
  }

  moveThumb() {
    const tab = this.tabs[this.activeIndex];
    if (!this.thumb || !tab) return;
    this.thumb.style.width = `${tab.offsetWidth}px`;
    this.thumb.style.height = `${tab.offsetHeight}px`;
    this.thumb.style.transform = `translate(${tab.offsetLeft}px, ${tab.offsetTop}px)`;
  }
}

if (!customElements.get('oracura-category-showcase')) {
  customElements.define('oracura-category-showcase', OracuraCategoryShowcase);
}
